import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia, resolveImg } from "@/lib/api";
import { cn } from "@/lib/utils";

export const ImageUploader = ({ value, onChange, label, testid, square = false, wide = false }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Image files lang po ang pwede");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      onChange(res.url);
      toast.success("Na-upload ang image");
    } catch (err) {
      toast.error("Hindi na-upload ang image");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const controls = (
    <>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testid ? `${testid}-upload` : undefined}>
          {uploading ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Ina-upload...</> : <><Upload className="mr-1.5 h-3.5 w-3.5" /> Mag-upload</>}
        </Button>
        {value ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}>
            <X className="mr-1 h-3.5 w-3.5" /> Alisin
          </Button>
        ) : null}
      </div>
      <Input className="h-9" placeholder="O i-paste ang image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `${testid}-url` : undefined} />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </>
  );

  // Wide mode: large 16:9 preview stacked above the controls — matches how the
  // hero image renders on the live site so admins can confirm swaps at a glance.
  if (wide) {
    return (
      <div>
        {label && <Label className="text-xs font-medium text-foreground">{label}</Label>}
        <div className="mt-1.5 space-y-2">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg border border-border bg-muted" data-testid={testid ? `${testid}-preview` : undefined}>
            {value ? (
              <img src={resolveImg(value)} alt="preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center px-4 text-center text-xs text-muted-foreground">Walang image — mag-upload o mag-paste ng URL para makita ang preview dito</div>
            )}
          </div>
          {controls}
        </div>
      </div>
    );
  }

  return (
    <div>
      {label && <Label className="text-xs font-medium text-foreground">{label}</Label>}
      <div className="mt-1.5 flex gap-3">
        <div className={cn("shrink-0 overflow-hidden rounded-lg border border-border bg-muted", square ? "h-20 w-20" : "h-20 w-28")}>
          {value ? (
            <img src={resolveImg(value)} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">Walang image</div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          {controls}
        </div>
      </div>
    </div>
  );
};
